import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useT } from '@/i18n/useT'

/**
 * The right-hand detail panel the redesign uses instead of a centred dialog:
 * a scrim, a fixed panel with a dark tile header, a scrolling body and a
 * sticky footer for the decision buttons.
 */
export function SideDrawer({
  open,
  onClose,
  width = 620,
  header,
  footer,
  children,
}: {
  open: boolean
  onClose: () => void
  width?: number
  header: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  // Esc closes, and the page behind must not scroll while the panel is up
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 1200 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          maxWidth: '96vw',
          background: 'var(--paper)',
          borderLeft: '1px solid var(--line)',
          zIndex: 1201,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-24px 0 60px rgba(0,0,0,0.35)',
        }}
      >
        {header}
        <div
          className="fx-drawer-body"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          {children}
        </div>
        {footer != null && (
          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--line)',
              background: 'var(--field)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** The dark header band shared by both drawers. */
export function DrawerHeader({
  title,
  subtitle,
  onClose,
  leading,
}: {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  leading?: ReactNode
}) {
  const { t } = useT()
  return (
    <div
      style={{
        padding: '16px 20px',
        background: 'var(--tile)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      {leading}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{title}</div>
        {subtitle != null && (
          <div style={{ fontSize: 12, color: 'var(--onTileMuted)', marginTop: 3 }}>{subtitle}</div>
        )}
      </div>
      <button
        type="button"
        aria-label={t('Close')}
        onClick={onClose}
        className="fx-icon-btn fx-icon-btn--onTile"
      >
        ✕
      </button>
    </div>
  )
}

/** Centred modal in the same language — used for the audit trail. */
export function CenterModal({
  open,
  onClose,
  width = 620,
  header,
  footer,
  children,
}: {
  open: boolean
  onClose: () => void
  width?: number
  header: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 1300 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width,
          maxWidth: '94vw',
          maxHeight: '90vh',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          zIndex: 1301,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
        }}
      >
        {header}
        <div style={{ overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer}
      </div>
    </div>,
    document.body,
  )
}

/** Tile-coloured header for CenterModal. */
export function ModalHeader({
  title,
  subtitle,
  onClose,
}: {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
}) {
  const { t } = useT()
  return (
    <div
      style={{
        padding: '14px 18px',
        background: 'var(--tile)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</div>
        {subtitle != null && (
          <div style={{ fontSize: 12, color: 'var(--onTileMuted)', marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      <button
        type="button"
        aria-label={t('Close')}
        onClick={onClose}
        className="fx-icon-btn fx-icon-btn--onTile"
      >
        ✕
      </button>
    </div>
  )
}
